/* Phase 8A -- Module Visibility store.
 *
 * Fetches the current user's per-module visibility map from
 * `GET /api/rbac/module-visibility` once on app boot (post-auth) and
 * exposes helpers for sidebar filtering + route guards.
 *
 * Phase 8B (bugfix): the preview-as-role feature is now fully isolated
 * from the real visibility state. It writes to `previewModules` /
 * `previewRole` only; the real `modules` map is never mutated. Consumers:
 *
 *   - Sidebar / BottomTabBar    -> use `sidebarModules()` (preview if set,
 *                                   else the real matrix)
 *   - ModuleGuard (route guard) -> uses the REAL `modules` -- preview must
 *                                   never redirect the actual session
 *
 * Not persisted -- per-session only.
 */
import { create } from "zustand";
import api from "@/lib/api";

export const useModuleVisibility = create((set, get) => ({
  modules: null,       // real map -- {hr: true, chat: true, ...}
  role: null,
  is_superuser: false,
  loading: false,
  loaded: false,
  error: null,

  // Preview overlay (client-side only)
  previewModules: null,
  previewRole: null,

  isVisible: (key) => {
    const m = get().modules;
    if (!m) return true; // fail-open while loading
    return m[key] === true;
  },

  /** What the sidebar/bottom-tab should render. Falls back to real map. */
  sidebarModules: () => {
    const s = get();
    return s.previewModules || s.modules;
  },

  firstVisiblePath: (fallback = "/") => {
    // Route guard uses this. Always operate on the REAL map so a preview
    // overlay never navigates the actual session.
    const m = get().modules;
    if (!m) return fallback;
    const priorities = ["home", "hr", "chat", "tasks", "projects", "sales_crm",
                        "orders", "dealers", "service", "reports", "admin"];
    for (const k of priorities) if (m[k]) {
      const route = {
        home: "/", hr: "/hr", chat: "/chat", tasks: "/tasks",
        projects: "/projects", sales_crm: "/sales/pipeline",
        orders: "/orders", dealers: "/dealers",
        service: "/service/tickets", reports: "/reports", admin: "/admin",
      }[k];
      if (route) return route;
    }
    return fallback;
  },

  refresh: async (force = false) => {
    if (get().loading) return;
    set({ loading: true, error: null });
    try {
      const { data } = await api.get(`/rbac/module-visibility${force ? "?force=1" : ""}`);
      set({
        modules: data.modules || {},
        role: data.role,
        is_superuser: !!data.is_superuser,
        loading: false, loaded: true, error: null,
      });
    } catch (e) {
      set({ loading: false, error: e?.message || "load_failed" });
    }
  },

  reset: () => set({
    modules: null, role: null, is_superuser: false, loaded: false,
    previewModules: null, previewRole: null,
  }),

  /** Phase 8B -- Client-side preview overlay. NEVER mutates `modules`. */
  setPreview: (targetRoleMap, targetRoleKey) => {
    set({ previewModules: targetRoleMap, previewRole: targetRoleKey });
  },

  clearPreview: () => set({ previewModules: null, previewRole: null }),
}));
